import {
  type FC,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import classNames from 'classnames'
import styled from 'styled-components'
import { Pagination, Zoom } from 'swiper/modules'
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import { IconStatsClose } from 'assets/svg/sprite'
import { ShowMore } from 'components/ShowMore/ShowMore'
import { ThreeDotsTrigger } from 'components-ref/atoms'
import { usePreviewContext } from 'context/previewContext'
import PreviewActions from 'features/Preview/PreviewActions/PreviewActions'
import { extractMedia } from 'helpers/media'
import { emitter } from 'services/emitter/emitter'
import { EventsEmitter } from 'services/emitter/events'
import { type IFile } from 'types/interfaces/IFile'
import { type IPost } from 'types/interfaces/ITypes'

import SinglePostPreviewAudio from './components/SinglePostPreviewAudio/SinglePostPreviewAudio'
import SinglePostPreviewImage from './components/SinglePostPreviewImage/SinglePostPreviewImage'
import SinglePostPreviewVideo from './components/SinglePostPreviewVideo/SinglePostPreviewVideo'

import 'swiper/css/bundle'

interface Props {
  post: IPost
  selectedSlide?: number
  showControls: boolean
  isFirstPost: boolean
  hasNextPage: boolean
  minimizeFn: (fileIndex: number, type: string) => void
  toggleControls: () => void
  isLastPost?: boolean
  setIsOptionsNavOpen?: (val: boolean) => void
  onOpenOptions?: () => void
  newPostPage?: boolean
  isInViewport?: boolean
  onClose?: () => void
  setCommentsActive?: (val: boolean) => void
  disablePreviewContextSync?: boolean
  independentPreviewVisibility?: boolean
  onZoomStateChange?: (zoomed: boolean) => void
}

const SinglePostPreviewMedia: FC<Props> = ({
  post,
  selectedSlide = 0,
  showControls,
  isFirstPost,
  hasNextPage,
  minimizeFn,
  toggleControls,
  isLastPost = false,
  onOpenOptions,
  newPostPage,
  isInViewport,
  onClose,
  setCommentsActive: setCommentsActiveProp,
  disablePreviewContextSync = false,
  independentPreviewVisibility = false,
  onZoomStateChange
}) => {
  const {
    query,
    selectedPost,
    selectedPostType,
    selectedPostData,
    minimized,
    setCommentsActive: setCommentsActiveContext,
    clearModal,
    updateSelectedPost
  } = usePreviewContext()

  const postFile = {
    userId: post.user_id,
    name: post.user?.display_name,
    text: post.body,
    mentions: post.tags,
    media: extractMedia(post),
    minimizedName: post.user?.display_name,
    minimizedAvatar:
      post.user?.cropped_avatar?.url ??
      post.user?.avatar?.url ??
      placeholderAvatar
  }

  const [localActiveSlide, setLocalActiveSlide] = useState(0)
  const swiperRef = useRef<SwiperClass | null>(null)

  const activeSlide = disablePreviewContextSync
    ? localActiveSlide
    : selectedSlide

  useLayoutEffect(() => {
    if (disablePreviewContextSync) return

    const swiper = swiperRef.current
    if (!swiper || swiper.destroyed) return

    if (swiper.activeIndex !== selectedSlide) {
      swiper.slideTo(selectedSlide, 0, false)
    }
  }, [selectedSlide, disablePreviewContextSync])

  const resetZoom = () => {
    const swiper = swiperRef.current
    if (!swiper?.zoom) return

    if (swiper.slides?.[swiper.activeIndex]) swiper.zoom.out()

    swiper.zoom.disable()
    swiper.allowSlideNext = true
    swiper.allowSlidePrev = true
    onZoomStateChange?.(false)
  }

useEffect(() => {
  const swiper = swiperRef.current

  if (!swiper || swiper.destroyed) return

  if (showControls) {
    swiper.zoom?.out()
    swiper.zoom?.disable()

    swiper.allowSlideNext = true
    swiper.allowSlidePrev = true

    onZoomStateChange?.(false)
  } else {
    swiper.zoom?.enable()

    swiper.allowSlideNext = false
    swiper.allowSlidePrev = false
  }
}, [showControls])

  
  useEffect(() => {
    const swiper = swiperRef.current
    if (!swiper || swiper.destroyed) return

    const swiperEl = swiper.el
    if (!swiperEl) return

    const handleWheelZoom = (e: WheelEvent) => {
      if (
        window.innerWidth < 768 ||
        !window.matchMedia('(pointer: fine)').matches
      ) {
        return
      }

      if (showControls || !swiper.zoom) return

      const activeSlideEl = swiper.slides?.[swiper.activeIndex]
      if (!activeSlideEl) return

      const zoomContainer =
        activeSlideEl.querySelector('.swiper-zoom-container')

      if (!zoomContainer) return

      e.preventDefault()
      e.stopPropagation()

      swiper.zoom.enable()

      const currentScale =
        typeof swiper.zoom.scale === 'number' ? swiper.zoom.scale : 1

      const zoomAmount = Math.abs(e.deltaY) > 40 ? 0.25 : 0.12
      const nextScale =
        e.deltaY < 0
          ? currentScale + zoomAmount
          : currentScale - zoomAmount

      const clampedScale = Math.max(1, Math.min(4, nextScale))

      if (clampedScale <= 1) {
        swiper.zoom.out()
        swiper.allowSlideNext = true
        swiper.allowSlidePrev = true
        onZoomStateChange?.(false)
        return
      }

      swiper.zoom.in(clampedScale)
      swiper.allowSlideNext = false
      swiper.allowSlidePrev = false
      onZoomStateChange?.(true)
    }

    swiperEl.addEventListener('wheel', handleWheelZoom, { passive: false })

    return () => {
      swiperEl.removeEventListener('wheel', handleWheelZoom)
    }
  }, [showControls, onZoomStateChange])

  const handleClose = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation()
    resetZoom()

    if (!showControls) toggleControls()

    if (onClose) {
      onClose()
      return
    }

    clearModal()
  }

  const setCommentsActive =
    setCommentsActiveProp ?? setCommentsActiveContext

  const renderMediaItem = (item: IFile, index: number) => {
    const type =
      item.type === 'photo'
        ? 'photo'
        : item.type === 'video'
          ? 'video'
          : item.url.includes('/sounds/originals')
            ? 'audio'
            : 'unknown'

    const key = `${type}-${index}`

    if (type === 'photo') {
      return (
        <SwiperSlide
          key={key}
          className={classNames(
            { 'swiper-no-swiping': minimized },
            minimized && 'minimized-photo'
          )}
        >
          <SinglePostPreviewImage
            mediaItem={item}
            postFile={postFile}
            isReel={selectedPostData?.is_reel ?? false}
            minimized={minimized}
            isFirstPost={isFirstPost}
            toggleControls={toggleControls}
            showControls={showControls}
            hasNextPage={hasNextPage}
            isLastPost={isLastPost}
            isInViewport={isInViewport}
            onOpenOptions={onOpenOptions}
          />
        </SwiperSlide>
      )
    }

    if (type === 'video') {

      return (
        <SwiperSlide
          key={key}
          className={classNames(
            { 'swiper-no-swiping': !showControls || minimized },
            minimized && 'minimized-video'
          )}
        >
          <SinglePostPreviewVideo
            mediaItem={item}
            postFile={postFile}
            minimized={minimized}
            isFirstPost={isFirstPost}
            isActive={!!isInViewport && index === activeSlide}
            showControls={showControls}
            toggleControls={toggleControls}
            videoUserId={post.user?.id}
            hasNextPage={hasNextPage}
            isLastPost={isLastPost}
            isInViewport={isInViewport}
            independentPreviewVisibility={independentPreviewVisibility}
          />
        </SwiperSlide>
      )
    }

    if (type === 'audio') {
      return (
        <SwiperSlide
          key={key}
          className={classNames(
            { 'swiper-no-swiping': !showControls || minimized },
            minimized && 'minimized-audio'
          )}
        >
          <SinglePostPreviewAudio
            mediaItem={item}
            postFile={postFile}
            minimized={minimized}
            isFirstPost={isFirstPost}
            isActive={index === activeSlide}
            showControls={showControls}
            toggleControls={toggleControls}
            hasNextPage={hasNextPage}
            isLastPost={isLastPost}
            minimizeFn={minimizeFn}
            post={post}
            index={index}
          />
        </SwiperSlide>
      )
    }

    return <div key={key}>Unsupported media</div>
  }

  return (
    <div className='preview__file--wrapper'>
      <PreviewClose
        $minimized={minimized}
        className='preview__close'
        onClick={handleClose}
      >
        {minimized ? (
          <IconStatsClose color='#A3A3A3' />
        ) : (
          <IconStatsClose />
        )}
      </PreviewClose>

      {showControls && (
        <ThreeDotsTrigger
          onClick={e => {
            e.stopPropagation()
            onOpenOptions?.()
          }}
        />
      )}

      {showControls && !minimized && (
        <>
          <div className='showMoreWrapper'>
            <div className='showMoreWrapper__background' />

            <ShowMore
              userId={postFile.userId}
              name={postFile.name}
              time={post.created_at}
              text={postFile.text ?? ''}
              mentions={postFile.mentions}
            />
          </div>

          <PreviewActions
            newPostPage={newPostPage}
            stats={{
              overallRevenue: post.overall_revenue,
              purchasesCount: post.purchases_count,
              purchasesSum: post.purchases_sum,
              tipsCount: post.tips_count,
              tipsSum: post.tips_sum,
              conversionRate: post.conversion_rate,
              uniqueImpressions: post.unique_impressions,
              views: post.views,
              uniqueViews: post.unique_views,
              price: post.price
            }}
            modelData={{
              postId: post.id,
              modelId: post.user.id,
              modelUsername: post.user.username,
              avatarSrc:
                post.user.avatar?.url ??
                post.user.cropped_avatar?.url ??
                placeholderAvatar
            }}
            query={query}
            postId={post.id}
            isLiked={post.isLiked ?? false}
            likes={post.likes_count ?? 0}
            comments={post.comment_count ?? 0}
            setCommentsActive={setCommentsActive}
            entityType={post.type}
          />
        </>
      )}

      {(post as any)?.missing ? (
        <div className='preview__missingPost'>
          Post No Longer Exists
        </div>
      ) : (
        <Swiper
          onSwiper={(swiper: SwiperClass) => {
            swiperRef.current = swiper

            if (showControls) swiper.zoom.disable()
            else swiper.zoom.enable()
          }}
          initialSlide={
            disablePreviewContextSync
              ? 0
              : selectedSlide
          }
          style={{ width: '100%', height: '100%' }}
          className='myInnerSwiper'
          pagination={{ dynamicBullets: true }}
          modules={[Pagination, Zoom]}
          zoom={{
            maxRatio: 4,
            minRatio: 1,
            toggle: false
          }}
          onZoomChange={(swiper: SwiperClass, scale: number) => {
            const zoomed = scale > 1

            swiper.allowSlideNext = !zoomed
            swiper.allowSlidePrev = !zoomed

            onZoomStateChange?.(zoomed)
          }}
/*           observer
 */          watchOverflow
          direction='horizontal'
          preventInteractionOnTransition
          resistanceRatio={0}
          onSlideChange={(swiper: SwiperClass) => {
            const nextSlide = swiper.activeIndex

            if (disablePreviewContextSync) {
              setLocalActiveSlide(nextSlide)
            }

            emitter.emit(EventsEmitter.STOP_ALL_AUDIO_POSTS)

            if (
              !disablePreviewContextSync &&
              typeof selectedPost === 'number' &&
              selectedPostData
            ) {
              updateSelectedPost(
                selectedPost,
                nextSlide,
                selectedPostType,
                selectedPostData,
                query
              )
            }
          }}
        >
          {postFile.media.map(renderMediaItem)}
        </Swiper>
      )}
    </div>
  )
}

export default SinglePostPreviewMedia

interface PreviewCloseProps {
  $minimized: boolean
}

const PreviewClose = styled.div<PreviewCloseProps>`
  ${props =>
    props.$minimized && {
      bottom: 'calc(50% - 2rem)'
    }}

  z-index: 99;
`